import { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import JSZip from 'jszip';
import { useBranch } from '../contexts/BranchContext';

const SHOP_NAME = 'ชาบู ฮิดะ ปิ้งย่าง';
const CARD_SIZES = {
  a5: { label: 'A5 (148 × 210 มม.)', wMm: 148, hMm: 210 },
  a6: { label: 'A6 (105 × 148 มม.)', wMm: 105, hMm: 148 }
};
const DPI = 300;
const mmToPx = mm => Math.round((mm / 25.4) * DPI);

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * วาดป้ายตั้งโต๊ะพร้อมพิมพ์ลง canvas — พื้นหลังไล่สีแดง/ดำเต็มการ์ด เว้นกล่องขาว
 * ไว้เฉพาะรอบ QR (ให้สแกนง่าย) โลโก้/ชื่อร้าน/สาขา/เลขโต๊ะ/QR/คำแนะนำ
 */
async function renderTableCard({ canvas, size, tableCode, branchName, url }) {
  const { wMm, hMm } = CARD_SIZES[size];
  const W = mmToPx(wMm);
  const H = mmToPx(hMm);
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  const logoSize = Math.round(W * 0.20);
  const nameFontSize = Math.round(W * 0.07);
  const branchFontSize = Math.round(W * 0.05);
  const tableFontSize = Math.round(W * 0.13);
  const qrSize = Math.round(W * 0.62);
  const captionFontSize = Math.round(W * 0.05);

  const logoY = Math.round(H * 0.045);
  const nameY = logoY + logoSize + Math.round(H * 0.015);
  const branchY = nameY + nameFontSize + Math.round(H * 0.008);
  const tableY = branchY + branchFontSize + Math.round(H * 0.02);
  const qrY = tableY + tableFontSize + Math.round(H * 0.02);
  const qrPad = Math.round(W * 0.035);
  const captionY = qrY + qrSize + qrPad + Math.round(H * 0.025);

  const borderInset = Math.round(W * 0.04);
  const borderRadius = Math.round(W * 0.03);
  const qrBoxRadius = Math.round(W * 0.02);

  // ให้แน่ใจว่าฟอนต์โหลดพร้อมก่อนวาดตัวหนังสือ ไม่งั้นบางเบราว์เซอร์จะวาดด้วยฟอนต์ fallback
  await Promise.all([
    document.fonts.load(`${nameFontSize}px Chonburi`),
    document.fonts.load(`${tableFontSize}px Chonburi`)
  ]);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  const grad = ctx.createLinearGradient(borderInset, borderInset, W - borderInset, H - borderInset);
  grad.addColorStop(0, '#dc2626');
  grad.addColorStop(1, '#1a1a1a');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.roundRect(borderInset, borderInset, W - borderInset * 2, H - borderInset * 2, borderRadius);
  ctx.fill();

  try {
    const logo = await loadImage('/logo.jpg');
    ctx.save();
    ctx.beginPath();
    ctx.arc(W / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(logo, (W - logoSize) / 2, logoY, logoSize, logoSize);
    ctx.restore();
  } catch (_) {
    // ไม่มีโลโก้ก็ยังพิมพ์การ์ดต่อได้ แค่ไม่มีรูป
  }

  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffffff';
  ctx.font = `${nameFontSize}px Chonburi`;
  ctx.fillText(SHOP_NAME, W / 2, nameY + nameFontSize * 0.8);

  ctx.fillStyle = '#ffe4e4';
  ctx.font = `${branchFontSize}px Chonburi`;
  ctx.fillText(`สาขา${branchName || ''}`, W / 2, branchY + branchFontSize * 0.85);

  ctx.fillStyle = '#ffffff';
  ctx.font = `${tableFontSize}px Chonburi`;
  ctx.fillText(`โต๊ะ ${tableCode}`, W / 2, tableY + tableFontSize * 0.85);

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.roundRect((W - qrSize) / 2 - qrPad, qrY - qrPad, qrSize + qrPad * 2, qrSize + qrPad * 2, qrBoxRadius);
  ctx.fill();

  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, url, { width: qrSize, margin: 1, color: { dark: '#1a1a1a', light: '#ffffff' } });
  ctx.drawImage(qrCanvas, (W - qrSize) / 2, qrY, qrSize, qrSize);

  ctx.fillStyle = '#ffe4e4';
  ctx.font = `${captionFontSize}px Chonburi`;
  ctx.fillText('สแกนเพื่อสั่งอาหาร', W / 2, captionY + captionFontSize * 0.8);
}

export default function QRCodeGenerator({ apiUrl }) {
  const { currentBranch, effectiveBranchId, isAllBranches } = useBranch();
  // QR code ฝัง branch slug ของ currentBranch ไว้ในตัวเอง — ถ้าเลือก "รวมทุกสาขา" อยู่
  // currentBranch จะ fallback ไปสาขาแรกตามลำดับตัวอักษรแทน ทำให้พิมพ์ QR ผิดสาขาไปติดโต๊ะจริง
  const blocked = isAllBranches || !effectiveBranchId;
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [cardSize, setCardSize] = useState('a6');
  const [rendering, setRendering] = useState(false);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const canvasRef = useRef(null);

  // window.location.origin.replace(...) เดิมคืนค่า truthy เสมอ (แค่ไม่ match ก็คืนของเดิม
  // เฉยๆ) เลยไม่เคยตกไปถึง fallback จริงบน production — โดเมนแอดมิน (admin.chabuhida.com)
  // เลยหลุดเข้าไปเป็น customerAppUrl ตรงๆ ทำให้ QR ที่พิมพ์ออกไปพาลูกค้าไปหน้าแอดมินแทน
  const customerAppUrl = import.meta.env.VITE_CUSTOMER_APP_URL ||
                         (window.location.hostname === 'localhost'
                           ? window.location.origin.replace('5173', '5174').replace('3500', '5174')
                           : 'https://order.chabuhida.com');

  useEffect(() => {
    if (blocked) return;
    loadTables();
  }, [apiUrl, blocked]);

  useEffect(() => {
    if (selectedTable && canvasRef.current) {
      setRendering(true);
      renderTableCard({
        canvas: canvasRef.current,
        size: cardSize,
        tableCode: selectedTable,
        branchName: currentBranch?.name,
        url: `${customerAppUrl}?branch=${encodeURIComponent(currentBranch?.slug ?? '')}&table=${encodeURIComponent(selectedTable)}&src=qr`
      }).finally(() => setRendering(false));
    }
  }, [selectedTable, cardSize, currentBranch]);

  const loadTables = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/tables`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      const data = result.data || result;
      setTables(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load tables:', err);
    }
  };

  const handleTableChange = (e) => {
    setSelectedTable(e.target.value);
  };

  const downloadQRCode = () => {
    if (!canvasRef.current || !selectedTable) return;
    const link = document.createElement('a');
    link.download = `QR-Table-${selectedTable}-${cardSize}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  // กรองโต๊ะตามช่วงเลขที่กรอก (เทียบตัวเลขในรหัสโต๊ะ ไม่สนใจเลข 0 นำหน้า เช่น "05" = 5)
  // ไม่กรอกช่องไหนเลย = เอาทุกโต๊ะเหมือนเดิม
  const tablesInRange = tables.filter((table) => {
    const num = parseInt(table.code, 10);
    if (isNaN(num)) return true; // รหัสโต๊ะที่ไม่ใช่ตัวเลข (เช่น VIP1) ไม่กรองออก
    if (rangeFrom !== '' && num < Number(rangeFrom)) return false;
    if (rangeTo !== '' && num > Number(rangeTo)) return false;
    return true;
  });

  // ดาวน์โหลดทีละไฟล์หลาย ๆ ครั้งด้วย link.click() วนลูป โดนเบราว์เซอร์ (โดยเฉพาะ Chrome)
  // บล็อกอัตโนมัติหลังไฟล์แรก ๆ แบบเงียบ ๆ (ถือเป็น "หลายดาวน์โหลดพร้อมกัน" น่าสงสัย) ทำให้
  // ดาวน์โหลดมาได้แค่โต๊ะเดียวทั้งที่เลือกไว้หลายสิบโต๊ะ — รวมเป็น .zip ไฟล์เดียวแทนเพื่อกันปัญหานี้
  const downloadAllQRCodes = async () => {
    if (tablesInRange.length === 0) return;
    setBulkDownloading(true);
    try {
      const zip = new JSZip();
      const tempCanvas = document.createElement('canvas');

      for (const table of tablesInRange) {
        try {
          await renderTableCard({
            canvas: tempCanvas,
            size: cardSize,
            tableCode: table.code,
            branchName: currentBranch?.name,
            url: `${customerAppUrl}?branch=${encodeURIComponent(currentBranch?.slug ?? '')}&table=${encodeURIComponent(table.code)}&src=qr`
          });

          const blob = await new Promise(resolve => tempCanvas.toBlob(resolve, 'image/png'));
          if (blob) zip.file(`QR-Table-${table.code}-${cardSize}.png`, blob);
        } catch (err) {
          console.error(`Failed to generate card for table ${table.code}:`, err);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.download = `QR-Tables-${cardSize}.zip`;
      link.href = URL.createObjectURL(zipBlob);
      link.click();
      URL.revokeObjectURL(link.href);
    } finally {
      setBulkDownloading(false);
    }
  };

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#fff3cd', color: '#856404', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — QR code ต้องระบุสาขาเดียวเสมอ
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการพิมพ์ QR ผิดสาขา
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>สร้าง QR Code</h2>
        <p>สร้างป้ายตั้งโต๊ะพร้อมพิมพ์ สำหรับลูกค้าสแกนสั่งอาหาร</p>
      </div>

      <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            ขนาดกระดาษ:
          </label>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {Object.entries(CARD_SIZES).map(([key, { label }]) => (
              <label key={key} style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem',
                padding: '0.65rem',
                border: cardSize === key ? '2px solid #667eea' : '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: cardSize === key ? 700 : 500,
                color: cardSize === key ? '#3730a3' : '#1f2937',
                background: cardSize === key ? '#eef2ff' : 'white'
              }}>
                <input
                  type="radio"
                  name="cardSize"
                  value={key}
                  checked={cardSize === key}
                  onChange={() => setCardSize(key)}
                  style={{ margin: 0 }}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            เลือกโต๊ะ:
          </label>
          <select
            className="input"
            value={selectedTable}
            onChange={handleTableChange}
            style={{ width: '100%', padding: '0.75rem' }}
          >
            <option value="">-- เลือกโต๊ะ --</option>
            {tables.map((table) => (
              <option key={table._id || table.id} value={table.code}>
                โต๊ะ {table.code}
              </option>
            ))}
          </select>
        </div>

        {selectedTable && (
          <>
            <div style={{
              textAlign: 'center',
              marginBottom: '1.5rem',
              padding: '1.5rem',
              background: '#f8f9fa',
              borderRadius: '8px',
              color: '#2d3748'
            }}>
              {/* .card h3 บังคับ color:#fff สำหรับพื้นการ์ดมืด — กล่องนี้พื้นสว่าง ต้องทับที่ h3 ตรงๆ */}
              <h3 style={{ marginBottom: '1rem', color: '#2d3748' }}>ป้ายโต๊ะ {selectedTable}</h3>
              <canvas
                ref={canvasRef}
                style={{ display: 'block', margin: '0 auto', maxWidth: '100%', height: 'auto', opacity: rendering ? 0.5 : 1 }}
              ></canvas>
              <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666' }}>
                URL: {customerAppUrl}?branch={currentBranch?.slug}&table={selectedTable}&src=qr
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
              <button
                className="button"
                onClick={downloadQRCode}
                style={{ width: '100%' }}
              >
                ดาวน์โหลดป้ายนี้ ({CARD_SIZES[cardSize].label})
              </button>
            </div>
          </>
        )}

        {tables.length > 0 && (
          <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              ดาวน์โหลดหลายโต๊ะ (เว้นว่าง = เอาทุกโต๊ะ):
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                type="number"
                className="input"
                placeholder="จากโต๊ะ"
                min="0"
                value={rangeFrom}
                onChange={(e) => setRangeFrom(e.target.value)}
                style={{ flex: 1, padding: '0.6rem', color: '#1f2937' }}
              />
              <span style={{ color: '#6b7280' }}>ถึง</span>
              <input
                type="number"
                className="input"
                placeholder="ถึงโต๊ะ"
                min="0"
                value={rangeTo}
                onChange={(e) => setRangeTo(e.target.value)}
                style={{ flex: 1, padding: '0.6rem', color: '#1f2937' }}
              />
            </div>
            <button
              className="button"
              onClick={downloadAllQRCodes}
              disabled={tablesInRange.length === 0 || bulkDownloading}
              style={{
                width: '100%',
                background: tablesInRange.length === 0 ? '#9ca3af' : '#28a745',
                cursor: bulkDownloading ? 'wait' : undefined
              }}
            >
              {bulkDownloading
                ? `⏳ กำลังสร้างไฟล์ (${tablesInRange.length} โต๊ะ)...`
                : tablesInRange.length === 0
                ? 'ไม่มีโต๊ะในช่วงที่เลือก'
                : `ดาวน์โหลดป้าย ${tablesInRange.length === tables.length ? 'ทุกโต๊ะ' : `${tablesInRange.length} โต๊ะ`} เป็น .zip (${tablesInRange.length}/${tables.length} · ${CARD_SIZES[cardSize].label})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
